import { useEffect, useMemo, useState } from "react"
import type { CurrentTaskVersionSummary, Task, TaskVersionSnapshot, TaskVersionSummary } from "@/schema"
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
} from "@ui/shad"
import { cn } from "@ui/utils"
import { format } from "date-fns"
import { ChevronRight, MoreHorizontal, RotateCcw } from "lucide-react"
import { toast } from "sonner"

import { useFetchTaskVersion, useInfiniteTaskVersions, useRestoreTaskVersion } from "@/hooks/api/tasks"
import { ConfirmDialog } from "@/components/dialogs"

const VERSION_CLUSTER_WINDOW_MS = 60_000

type DisplaySnapshot = (TaskVersionSummary | CurrentTaskVersionSummary) & {
  id?: string
  isCurrent?: boolean
}

interface VersionCluster {
  key: string
  snapshots: DisplaySnapshot[]
}

function actorKey(snapshot: DisplaySnapshot) {
  return snapshot.actor?.id ?? "unknown"
}

function clusterVersions(snapshots: DisplaySnapshot[]) {
  return snapshots.reduce<VersionCluster[]>((clusters, snapshot) => {
    const currentCluster = clusters.at(-1)
    const clusterAnchor = currentCluster?.snapshots[0]
    const withinWindow =
      clusterAnchor &&
      new Date(clusterAnchor.created_at).getTime() - new Date(snapshot.created_at).getTime() <=
        VERSION_CLUSTER_WINDOW_MS

    if (currentCluster && clusterAnchor && actorKey(clusterAnchor) === actorKey(snapshot) && withinWindow) {
      currentCluster.snapshots.push(snapshot)
    } else {
      clusters.push({
        key: `${snapshot.id ?? "current"}-${snapshot.created_at}`,
        snapshots: [snapshot],
      })
    }

    return clusters
  }, [])
}

function VersionDetails({ snapshot }: { snapshot: DisplaySnapshot }) {
  return (
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-medium">{format(new Date(snapshot.created_at), "MMM d, h:mm a")}</span>
      <span className="text-muted-foreground mt-0.5 block truncate text-xs">
        {snapshot.actor?.name ?? "Unknown user"}
      </span>
    </span>
  )
}

function ViewingBadge() {
  return (
    <Badge
      variant="outline"
      size="xs"
      className="border-green-border bg-green-background text-green-foreground shrink-0"
    >
      Viewing
    </Badge>
  )
}

interface TaskVersionHistoryProps {
  taskId: string
  initialVersionId?: string
  onPreview: (version?: TaskVersionSnapshot) => void
  onRestored: (task: Task) => void
}

export function TaskVersionHistory({ taskId, initialVersionId, onPreview, onRestored }: TaskVersionHistoryProps) {
  const versionsQuery = useInfiniteTaskVersions(taskId)
  const restoreVersion = useRestoreTaskVersion(taskId)
  const [expandedClusters, setExpandedClusters] = useState<Set<string>>(new Set())
  const [selectedVersionId, setSelectedVersionId] = useState(initialVersionId)
  const [restoreCandidateId, setRestoreCandidateId] = useState<string>()
  const selectedVersionQuery = useFetchTaskVersion(taskId, selectedVersionId)

  useEffect(() => {
    setSelectedVersionId(initialVersionId)
  }, [initialVersionId])

  const clusters = useMemo(() => {
    if (!versionsQuery.data?.current) return []

    return clusterVersions([{ ...versionsQuery.data.current, isCurrent: true }, ...versionsQuery.data.versions])
  }, [versionsQuery.data])

  const selectedVersionMatchesCurrent = Boolean(
    selectedVersionId &&
      selectedVersionQuery.data &&
      versionsQuery.data?.current &&
      selectedVersionQuery.data.created_at === versionsQuery.data.current.created_at
  )

  function isSnapshotSelected(snapshot: DisplaySnapshot) {
    if (snapshot.id) return snapshot.id === selectedVersionId
    return !selectedVersionId || selectedVersionMatchesCurrent
  }

  useEffect(() => {
    if (!selectedVersionId || selectedVersionQuery.isError) {
      if (selectedVersionQuery.isError) setSelectedVersionId(undefined)
      onPreview(undefined)
    } else {
      onPreview(selectedVersionQuery.data)
    }
  }, [onPreview, selectedVersionId, selectedVersionQuery.data, selectedVersionQuery.isError])

  useEffect(() => () => onPreview(undefined), [onPreview])

  function toggleCluster(key: string) {
    setExpandedClusters((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function selectSnapshot(snapshot: DisplaySnapshot) {
    setSelectedVersionId(snapshot.id)
  }

  function confirmRestore() {
    if (!restoreCandidateId) return

    restoreVersion.mutate(restoreCandidateId, {
      onSuccess: (task) => {
        setRestoreCandidateId(undefined)
        setSelectedVersionId(undefined)
        onPreview(undefined)
        onRestored(task)
        toast.success("Task version restored")
      },
    })
  }

  function VersionActions({ versionId }: { versionId: string }) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="iconXs" className="mr-2 shrink-0 self-center" aria-label="Version actions">
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-0">
          <DropdownMenuItem className="px-1.5 py-1 text-xs" onSelect={() => setRestoreCandidateId(versionId)}>
            <RotateCcw className="size-3!" />
            Restore this version
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <div>
      {versionsQuery.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : versionsQuery.isError ? (
        <div className="rounded-md border border-dashed py-8 text-center">
          <p className="text-sm font-medium">Unable to load version history</p>
          <Button variant="outline" size="xs" className="mt-3" onClick={() => versionsQuery.refetch()}>
            Try again
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {clusters.map((cluster) => {
            const snapshot = cluster.snapshots[0]
            const grouped = cluster.snapshots.length > 1
            const expanded = expandedClusters.has(cluster.key)
            const clusterSelected = cluster.snapshots.some(isSnapshotSelected)
            const snapshotSelected = isSnapshotSelected(snapshot)

            return (
              <div
                key={cluster.key}
                className={cn(
                  "overflow-hidden rounded-xl border",
                  clusterSelected && "border-border bg-background shadow-sm"
                )}
              >
                {grouped ? (
                  <div className="flex items-start">
                    <button
                      type="button"
                      className="hover:bg-muted/50 flex min-w-0 flex-1 items-start gap-2 px-3 py-3 text-left"
                      onClick={() => toggleCluster(cluster.key)}
                      aria-expanded={expanded}
                    >
                      <ChevronRight
                        className={cn(
                          "text-muted-foreground mt-0.5 size-3.5 shrink-0 transition-transform",
                          expanded && "rotate-90"
                        )}
                      />
                      <VersionDetails snapshot={snapshot} />
                      {clusterSelected ? <ViewingBadge /> : null}
                      <div className="text-muted-foreground flex shrink-0 flex-col items-end gap-1 text-right text-xs">
                        <div className="flex items-center gap-1">
                          {!snapshot.id ? <span>Current version</span> : null}
                        </div>
                        <span className="block">{cluster.snapshots.length} edits</span>
                      </div>
                    </button>
                  </div>
                ) : (
                  <div className="flex items-start">
                    <button
                      type="button"
                      className="hover:bg-muted/50 flex min-w-0 flex-1 items-start gap-2 px-3 py-3 text-left"
                      onClick={() => selectSnapshot(snapshot)}
                    >
                      <ChevronRight className="text-muted-foreground mt-0.5 size-3.5 shrink-0" />
                      <VersionDetails snapshot={snapshot} />
                      {!snapshot.id ? (
                        <span className="text-muted-foreground shrink-0 text-xs">Current version</span>
                      ) : null}
                      {snapshotSelected ? <ViewingBadge /> : null}
                    </button>
                    {snapshot.id ? <VersionActions versionId={snapshot.id} /> : null}
                  </div>
                )}

                {grouped && expanded ? (
                  <div className="bg-muted/20 space-y-1 border-t p-2">
                    {cluster.snapshots.map((groupedSnapshot) =>
                      groupedSnapshot.id ? (
                        <div
                          key={groupedSnapshot.id}
                          className={cn(
                            "flex items-center rounded-md",
                            isSnapshotSelected(groupedSnapshot) && "bg-background shadow-sm"
                          )}
                        >
                          <button
                            type="button"
                            className="hover:bg-background flex min-w-0 flex-1 items-center justify-between rounded-md px-2 py-1.5 text-left text-xs"
                            onClick={() => selectSnapshot(groupedSnapshot)}
                          >
                            <span>{format(new Date(groupedSnapshot.created_at), "h:mm:ss a")}</span>
                            {isSnapshotSelected(groupedSnapshot) ? (
                              <ViewingBadge />
                            ) : (
                              <span className="text-muted-foreground">View version</span>
                            )}
                          </button>
                          <VersionActions versionId={groupedSnapshot.id} />
                        </div>
                      ) : (
                        <button
                          key="current"
                          type="button"
                          className={cn(
                            "flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-xs",
                            isSnapshotSelected(groupedSnapshot) && "bg-background shadow-sm"
                          )}
                          onClick={() => selectSnapshot(groupedSnapshot)}
                        >
                          <span>{format(new Date(groupedSnapshot.created_at), "h:mm:ss a")}</span>
                          <div className="flex items-center gap-1">
                            <span className="text-muted-foreground">Current version</span>
                            {isSnapshotSelected(groupedSnapshot) ? <ViewingBadge /> : null}
                          </div>
                        </button>
                      )
                    )}
                  </div>
                ) : null}
              </div>
            )
          })}

          {versionsQuery.hasNextPage ? (
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              disabled={versionsQuery.isFetchingNextPage}
              onClick={() => versionsQuery.fetchNextPage()}
            >
              {versionsQuery.isFetchingNextPage ? "Loading…" : "Load older versions"}
            </Button>
          ) : null}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(restoreCandidateId)}
        onOpenChange={(open) => !open && setRestoreCandidateId(undefined)}
        title={"Restore this task version?"}
        description={
          "The selected title and description will become the latest version. The current title and description will remain available in version history."
        }
        dismiss={restoreVersion.isPending ? "none" : "explicit"}
        cancel={{ onClick: () => setRestoreCandidateId(undefined), disabled: restoreVersion.isPending }}
        action={{ label: "Restore version", onClick: confirmRestore, pending: restoreVersion.isPending }}
      />
    </div>
  )
}
